import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface WaiverModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  organizerName: string;
  isLoading?: boolean;
}

const WaiverModal: React.FC<WaiverModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  organizerName,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [showFullWaiver, setShowFullWaiver] = useState(false);
  const [hasReadWaiver, setHasReadWaiver] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShowFullWaiver(false);
      setHasReadWaiver(false);
      setAcceptedTerms(false);
    }
  }, [isOpen]);

  const handleReadWaiver = () => {
    setShowFullWaiver(true);
    setHasReadWaiver(true);
  };

  const handleAccept = () => {
    if (!acceptedTerms) {
      return;
    }
    onAccept();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-primary-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">
                {t('waiver.title', 'Waiver & Release of Liability')}
              </h2>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
                disabled={isLoading}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-primary-100 text-sm mt-1">
              {t('waiver.organizedBy', 'Organized by {{organizer}}', { organizer: organizerName })}
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
            {/* Summary */}
            <div className="mb-4">
              <p className="text-gray-700 leading-relaxed">
                {t('waiver.summary', 
                  'By participating in this ride organized by {{organizer}}, you acknowledge that cycling involves inherent risks including but not limited to injury, property damage, or death. You agree to assume all risks and release the organizer from any liability.',
                  { organizer: organizerName }
                )}
              </p>
            </div>

            {/* Read Full Waiver Button */}
            {!showFullWaiver && (
              <button
                onClick={handleReadWaiver}
                className="w-full py-3 px-4 border-2 border-primary-600 text-primary-600 rounded-lg font-medium hover:bg-primary-50 transition-colors mb-4"
              >
                {t('waiver.readFullWaiver', 'Read Full Waiver')}
              </button>
            )}

            {/* Full Waiver Text */}
            {showFullWaiver && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('waiver.fullWaiverTitle', 'RELEASE AND WAIVER OF LIABILITY')}
                  </h3>
                  <button
                    onClick={() => setShowFullWaiver(false)}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    {t('waiver.hideFullWaiver', 'Hide Full Waiver')}
                  </button>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                  <p className="text-gray-700 text-sm whitespace-pre-line leading-relaxed">
                    {t('waiver.fullWaiverText',
                      `In consideration of being permitted to participate in any cycling activity organized by {{organizer}}, I, for myself, my heirs, personal representatives or assigns, do hereby release, waive, discharge, and covenant not to sue {{organizer}}, its officers, employees, and agents from any and all liability, claims, demands, actions, and causes of action whatsoever arising out of or related to any loss, damage, or injury, including death, that may be sustained by me, or to any property belonging to me, while participating in such activity.

I am fully aware of the risks and hazards connected with cycling activities, including but not limited to: falls, collisions with vehicles, other cyclists, pedestrians, or fixed objects, mechanical failures, road conditions, weather conditions, and the negligence of other participants.

I hereby assume full responsibility for any risk of bodily injury, death, or property damage arising out of or related to my participation in cycling activities, whether caused by the negligence of {{organizer}} or otherwise.

I agree to obey all traffic laws, wear appropriate safety gear including a helmet, and follow the instructions of ride leaders. I certify that I am physically fit and have sufficiently trained for participation in this activity.

I have read this release and waiver of liability, fully understand its terms, and understand that I am giving up substantial rights by signing it. I sign it freely and voluntarily without any inducement.`,
                      { organizer: organizerName }
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Checkbox */}
            <div className="flex items-start gap-3 mb-4">
              <input
                type="checkbox"
                id="acceptTerms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                disabled={!hasReadWaiver || isLoading}
                className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50"
              />
              <label 
                htmlFor="acceptTerms" 
                className={`text-sm ${hasReadWaiver ? 'text-gray-700' : 'text-gray-400'}`}
              >
                {t('waiver.acknowledgment', 'I have read and understand the waiver, and I voluntarily agree to its terms')}
              </label>
            </div>

            {!hasReadWaiver && (
              <p className="text-amber-600 text-sm mb-4">
                ⚠️ {t('waiver.mustReadFirst', 'Please read the full waiver before accepting')}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleAccept}
              disabled={!acceptedTerms || isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading && (
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {t('waiver.acceptAndJoin', 'Accept & Join Ride')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaiverModal;

